import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import DocumentVaultAccounts from './DocumentVaultAccounts';
import InvestmentEntityDocumentSlideOver from './InvestmentEntityDocumentSlideOver';

const INVESTMENT_ENTITY_QUERY = gql(`
  query InvestmentEntityDocuments($id: ID!) {
    investmentEntity(id: $id) {
      investmentEntityDocuments {
        id
        type
        name
        createdAt
        documentDate
      }
    }
  }
`);

const InvestmentEntityDocuments: FC = () => {
  const [documentOnDisplay, setDocumentOnDisplay] = useState('');
  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITY_QUERY, {
    variables: {
      id: investmentEntityId,
    },
  });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const documents = data.investmentEntity.investmentEntityDocuments;

  const groupedDocuments = [...documents]
    .sort((a, b) => {
      // Sort by documentDate first then by createdAt as fallback
      const dateA = dayjs(a.documentDate || a.createdAt);
      const dateB = dayjs(b.documentDate || b.createdAt);
      return dateB.diff(dateA);
    })
    .reduce((groups, doc) => {
      const year = dayjs(doc.documentDate).get('year') || 'Others';
      return { ...groups, [year]: [...(groups[year] || []), doc] };
    }, {} as Record<string, typeof documents>);

  return (
    <>
      <InvestmentEntityDocumentSlideOver
        open={!!documentOnDisplay}
        documentId={documentOnDisplay}
        onClose={() => setDocumentOnDisplay('')}
        investmentEntityId={investmentEntityId}
      />
      {Object.entries(groupedDocuments)
        .sort(([yearA], [yearB]) => {
          // Sort years in descending order (most recent first)
          if (yearA === 'Others') return 1;
          if (yearB === 'Others') return -1;
          return parseInt(yearB) - parseInt(yearA);
        })
        .map(([year, documents]) => (
          <div key={year}>
            <h2 className="text-2xl font-bold mb-4">{year}</h2>
            <DataTable
              data={documents}
              onClick={document => setDocumentOnDisplay(document.id)}
              columns={[
                {
                  label: 'Name',
                  fieldName: 'name',
                },
                {
                  label: 'Type',
                  fieldName: 'type',
                  type: 'INVESTMENT_ENTITY_DOCUMENT_TYPE',
                },
                {
                  label: 'Added on',
                  fieldName: 'createdAt',
                  type: 'DATE',
                },
              ]}
            />
          </div>
        ))}
      <div className="my-16">
        <DocumentVaultAccounts investmentEntityId={investmentEntityId} />
      </div>
    </>
  );
};

export default InvestmentEntityDocuments;
